import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import CompanyAutocompleteInput from '../shared/CompanyAutocompleteInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateSupplierModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    tax_id: '',
    payment_terms: '',
    category: '',
    rating: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };

      // Convert rating to number if provided
      if (payload.rating) {
        payload.rating = parseInt(payload.rating);
      } else {
        delete payload.rating;
      }

      // Remove empty fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          delete payload[key];
        }
      });

      const response = await fetch(`${API_URL}/api/procurement/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to create supplier');

      toast({
        title: 'Success',
        description: 'Supplier created successfully'
      });

      // Reset form
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
    website: '',
        address: '',
        tax_id: '',
        payment_terms: '',
        category: '',
        rating: '',
        status: 'active'
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast({
        title: 'Error',
        description: 'Failed to create supplier',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2B2D42]">Create New Supplier</h2>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#2B2D42] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Name - Required (with cross-platform autocomplete) */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <CompanyAutocompleteInput
              testid="supplier-input-company-name"
              value={formData.company_name}
              onChange={(v) => setFormData((p) => ({ ...p, company_name: v }))}
              onSelect={(s) => setFormData((p) => ({
                ...p,
                company_name: s.company_name || p.company_name,
                contact_name: s.contact_person || p.contact_name,
                email:        s.email || p.email,
                phone:        s.phone || p.phone,
                tax_id:       s.trn || s.tax_id || p.tax_id,
                address:      p.address,
              }))}
              placeholder="Type to search counterparties, suppliers, leads…"
            />
          </div>

          {/* Contact Person */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Contact Person
            </label>
            <input
              type="text"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Enter contact person name"
            />
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="supplier@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Website
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="https://www.example.com"
              data-testid="supplier-create-website-input"
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Enter physical address"
            />
          </div>

          {/* Tax ID & Payment Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Tax ID
              </label>
              <input
                type="text"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="Tax identification number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Payment Terms
              </label>
              <input
                type="text"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="e.g., Net 30, Net 60"
              />
            </div>
          </div>

          {/* Category & Rating */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Category
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                placeholder="e.g., Raw Materials, Logistics"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Rating (1-5 stars)
              </label>
              <select
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              >
                <option value="">No rating</option>
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
              </select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Supplier'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSupplierModal;